import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/Table/DataTable";
import { IconTrash } from "../../components/icons/icons";
import TablePaginationActions from "../../components/Table/tablePagination";
import AddRecommendedProviderModal from "./AddRecommendedProviderModal";

export default function RecommendedList() {
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"doctors" | "services">("doctors");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locations, setLocations] = useState<Record<number, any[]>>({});
  const [searchQuery, setSearchQuery] = useState("");
  type DoctorSearchField = "name" | "mobile" | "speciality";
  type ServiceProviderSearchField = "name" | "mobile" | "service";
  const [searchField, setSearchField] = useState<DoctorSearchField | ServiceProviderSearchField>("name");

  const [locationLoading, setLocationLoading] = useState(false);
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchRecommended();
  }, []);

  const fetchRecommended = async (query?: string, field?: string) => {
    try {
      setLoading(true);

      const searchValue = query !== undefined ? query : searchQuery;
      const searchByField = field || searchField;

      // Doctor params
      const doctorParams: Record<string, string> = {};
      if (activeTab === "doctors" && searchValue.trim()) {
        doctorParams[searchByField] = searchValue.trim();
      }

      // Service provider params
      const serviceParams: Record<string, string> = {};
      if (activeTab === "services" && searchValue.trim()) {
        if (searchByField === "service") {
          // convert to underscore format
          serviceParams[searchByField] = searchValue
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_");
        } else {
          serviceParams[searchByField] = searchValue.trim();
        }
      }

      const doctorQuery = new URLSearchParams(doctorParams).toString();
      const serviceQuery = new URLSearchParams(serviceParams).toString();

      const [doctorsRes, servicesRes] = await Promise.all([
        apiClient<any>(`/v1/resource/recommended-providers/list?${doctorQuery}`, { method: "GET" }),
        apiClient<any>(`/v1/resource/recommended-service-providers/list?${serviceQuery}`, { method: "GET" }),
      ]);

      setDoctors(doctorsRes.data || []);
      setServices(servicesRes.data || []);
      await fetchLocationsForUsers((doctorsRes.data || []).concat(servicesRes.data || []));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationsForUsers = async (users: any[]) => {
    try {
      setLocationLoading(true);
      const locationData: Record<number, any[]> = {};

      await Promise.all(
        users.map(async (userRow) => {
          const userId = userRow.user?.id;
          if (userId) {
            try {
              const res = await apiClient<any>(
                `/v1/resource/UserLocation?user_id=${userId}&is_confirmed=1`
              );
              locationData[userId] = res?.data?.user_locations || [];
            } catch (err) {
              console.error(`Failed to fetch locations for user ${userId}`, err);
              locationData[userId] = [];
            }
          }
        })
      );

      setLocations(locationData);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const endpoint =
        activeTab === "doctors"
          ? `/v1/resource/recommended-providers/${row.id}`
          : `/v1/resource/recommended-service-providers/${row.id}`;

      await apiClient(endpoint, { method: "DELETE" });
      fetchRecommended();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Table columns
  const serialColumn = {
    key: "sno",
    label: "S.No",
    render: (_row: any, index: number) => page * rowsPerPage + index + 1,
  };

  const doctorColumns = [
    {
      key: "provider_info",
      label: "Provider Info",
      align: 'left',
      render: (row: any) => {
        const user = row.user || {};
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <strong>{`${user.first_name || ""} ${user.last_name || ""}`.trim()}</strong>
            <span style={{ fontSize: "13px", color: "#555" }}>{user.email || "-"}</span>
            <span style={{ fontSize: "13px", color: "#777" }}>{user.mobile || "-"}</span>
          </div>
        );
      },
    },
    {
      key: "specialities",
      label: "Specialities",
      align: 'left',
      render: (row: any) => {
        const specialities = row.provider?.provider_speciality || [];
        if (!specialities.length) return "-";
        return specialities
          .map((s: any) => s?.speciality_detail?.name)
          .filter(Boolean)
          .join(", ");
      },
    },
    {
      key: "location",
      label: "Address",
      align: 'left',
      render: (row: any) => {
        const userId = row.user?.id;
        if (!userId || !locations[userId] || locations[userId].length === 0) return "-";

        // Show first confirmed location's display address
        return locations[userId][0]?.additional_info?.display_address || "-";
      }
    },
    { key: "qualification", label: "Qualification", align: 'left', render: (row: any) => row.provider?.additional_info?.qualification || "-" },
    { key: "license", label: "License No", render: (row: any) => row.provider?.additional_info?.license_no || "-" },
  ];

  const serviceColumns = [
    {
      key: "provider_info",
      label: "Service Provider Info",
      align: "left",
      render: (row: any) => {
        const user = row.user || {};
        return (
          <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
            <span style={{ fontWeight: 600, color: "#111" }}>
              {`${user.first_name || ""} ${user.last_name || ""}`.trim() || "-"}
            </span>
            <span style={{ fontSize: "13px", color: "#555" }}>
              {user.email || "-"}
            </span>
            <span style={{ fontSize: "13px", color: "#555" }}>
              {user.mobile || "-"}
            </span>
            <span style={{ fontSize: "13px", color: "#555" }}>
              GSTIN: {row.service_provider?.additional_info?.GSTIN || "-"}
            </span>
          </div>
        );
      },
    },

    {
      key: "services",
      label: "Services",
      align: "left",
      render: (row: any) => {
        const services = row.service_provider?.services || [];
        if (!services.length) return "-";
        const formatServiceName = (name: string) =>
          name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

        return services
          .map((s: any) => formatServiceName(s.services))
          .join(", ");
      },
    },
    {
      key: "location",
      label: "Address",
      align: 'left',
      render: (row: any) => {
        const userId = row.user?.id;
        if (!userId || !locations[userId] || locations[userId].length === 0) return "-";

        // Show first confirmed location's display address
        return locations[userId][0]?.additional_info?.display_address || "-";
      }
    },
  ];

  const doctorColumnsWithSno = [serialColumn, ...doctorColumns];
  const serviceColumnsWithSno = [serialColumn, ...serviceColumns];

  const actions = [{ label: "Delete", icon: <IconTrash />, color: "#d32f2f", onClick: handleDelete }];

  return (
    <>
      <div className="flex justify-between items-center mt-4 px-6">
        <div className="space-x-4">
          <button className={`px-6 py-2 rounded-full font-semibold ${activeTab === "doctors" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"}`} onClick={() => setActiveTab("doctors")}>Veterinarians</button>
          <button className={`px-6 py-2 rounded-full font-semibold ${activeTab === "services" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"}`} onClick={() => setActiveTab("services")}>Service Providers</button>
        </div>

        <button onClick={() => setShowAddModal(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg shadow hover:bg-green-700">
          + Add Recommended Provider
        </button>
      </div>
      <div className="flex items-center gap-2 mb-2 mt-6 px-6">
        <select
          value={searchField}
          onChange={(e) => {
            const newField = e.target.value as typeof searchField;
            setSearchField(newField);
            setPage(0);
            fetchRecommended(searchQuery, newField);
          }}
          className="border px-2 py-1 rounded"
        >
          <option value="name">Name</option>
          <option value="mobile">Mobile</option>
          {activeTab === "doctors" && <option value="speciality">Speciality</option>}
          {activeTab === "services" && <option value="service">Service</option>}
        </select>

        <input
          type="text"
          placeholder={`Search by ${searchField}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border px-2 py-1 rounded flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(0);
              fetchRecommended();
            }
          }}
        />

        <button
          onClick={() => {
            setPage(0);
            fetchRecommended();
          }}
          className="bg-blue-600 text-white px-3 py-1 rounded"
        >
          Search
        </button>

        <button
          onClick={() => {
            setSearchQuery("");
            setPage(0);
            fetchRecommended("", searchField); // pass cleared query
          }}
          className="bg-gray-400 text-white px-3 py-1 rounded"
        >
          Clear
        </button>


      </div>



      <div className="p-6">
        <DynamicTable
          columns={activeTab === "doctors" ? doctorColumnsWithSno : serviceColumnsWithSno}
          data={activeTab === "doctors" ? doctors : services}
          loading={loading}
          emptyMessage={activeTab === "doctors" ? "No recommended veterinarians found" : "No recommended service providers found"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={(activeTab === "doctors" ? doctors : services).length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          actions={actions}
          ActionsComponent={TablePaginationActions}
        />
      </div>

      {showAddModal && (
        <AddRecommendedProviderModal
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchRecommended}
        />
      )}
    </>
  );
}
